import React from "react";

import { DiffSyncPage, DiffSyncRecord } from "@megaron/diff-sync-webapp";
import { useClientManager } from "@megaron/react-clients";
import {
  ShippingDiffSyncChannel,
  UnitTemplate,
  UnitTemplateComponents,
  unitTemplateComponentsSerializer,
} from "@megaron/shipping-contracts";

type UnitTemplateDiffValue = UnitTemplate & Record<string, unknown>;

const channel: ShippingDiffSyncChannel = "sheetsUnitTemplate";

export const UnitTemplatesSyncDiffPage: React.FC = () => {
  const client = useClientManager("shipping");
  const pullQuery = client.diffQuery().useQuery({ channel, isPullable: true });
  const pushQuery = client.diffQuery().useQuery({ channel, isPushable: true });
  const allDiffsQuery = client.diffQuery().useQuery({ channel });
  const channelQuery = client.diffChannelQuery().useQuery({ channel });
  const refreshMutation = client.refreshDiffs().useMutation();
  const pullMutation = client.pullDiffs().useMutation();
  const pushMutation = client.pushDiffs().useMutation();

  const refetchDiffs = async () => {
    await Promise.all([pullQuery.refetch(), pushQuery.refetch(), allDiffsQuery.refetch()]);
  };

  const refresh = async () => {
    await refreshMutation.mutateAsync({ channel });
    await refetchDiffs();
  };

  const pull = async (diffIds: string[]) => {
    try {
      await pullMutation.mutateAsync({ channel, diffIds });
    } finally {
      await refetchDiffs();
    }
  };

  const push = async (diffIds: string[]) => {
    try {
      await pushMutation.mutateAsync({ channel, diffIds });
    } finally {
      await refetchDiffs();
    }
  };

  return (
    <DiffSyncPage<UnitTemplateDiffValue>
      title="Synchronizacja Google Sheets - szablony jednostek"
      pullableDiffs={pullQuery.data as DiffSyncRecord<UnitTemplateDiffValue>[] | undefined}
      pushableDiffs={pushQuery.data as DiffSyncRecord<UnitTemplateDiffValue>[] | undefined}
      errorDiffs={
        allDiffsQuery.data?.filter((diff) => !!diff.error) as DiffSyncRecord<UnitTemplateDiffValue>[] | undefined
      }
      directionLabels={{ pull: "Do megarax.net", push: "Do Google Sheets" }}
      labels={unitTemplateLabels}
      formatters={{ components: formatComponents }}
      alwaysVisibleColumns={["id", "label", "unit", "category", "complexity", "isMandatory", "components"]}
      remoteResourceUrl={channelQuery.data?.remoteResourceUrl}
      isLoading={
        pullQuery.isLoading ||
        pushQuery.isLoading ||
        allDiffsQuery.isLoading ||
        pullQuery.isFetching ||
        pushQuery.isFetching ||
        allDiffsQuery.isFetching
      }
      isRefreshing={refreshMutation.isLoading}
      isExecuting={pullMutation.isLoading || pushMutation.isLoading}
      onRefresh={refresh}
      onPull={pull}
      onPush={push}
    />
  );
};

const unitTemplateLabels = {
  id: "ID",
  label: "Nazwa",
  unit: "Jednostka",
  category: "Kategoria",
  complexity: "Złożoność",
  isMandatory: "Obowiązkowy",
  components: "Komponenty",
} satisfies Partial<Record<keyof UnitTemplateDiffValue, string>>;

const formatComponents = (value: unknown) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return "-";

  return (
    <span css={{ whiteSpace: "pre-line" }}>
      {unitTemplateComponentsSerializer.serialize(value as UnitTemplateComponents)}
    </span>
  );
};
